import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { updateCardAccount } from '../../../store/slices/authSlice';
import { apiGetShopCards, apiBuyCard } from '../../../api/cardApi';
import type { Card, CardRarity } from '../../../types/models/card';
import CardDetailModal from '../../Cards/CardDetailModal';

const RARITY_COLOR: Record<CardRarity, string> = {
    legendary: '#f0c040',
    epic:      '#a855f7',
    rare:      '#3b82f6',
    common:    '#9ca3af',
};

const RARITIES: CardRarity[] = ['common', 'rare', 'epic', 'legendary'];

// ─── Styled ───────────────────────────────────────────────────────────────────

const Container = styled.div`
  padding: 24px 16px 80px;
  min-height: calc(100vh - 70px);
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 4px;
`;

const PageSub = styled.p`
  font-size: 13px;
  color: rgba(255,255,255,0.4);
  margin: 0 0 20px;
`;

const FiltersColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border-radius: 7px;
  border: 1px solid rgba(255,255,255,0.14);
  background-color: rgba(255,255,255,0.05);
  color: #fff;
  font-size: 13px;
  outline: none;
  &:focus { border-color: rgba(255,215,0,0.5); }
  option { background-color: #1a1a1a; }
`;

const ResultCount = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,0.3);
  margin-bottom: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const CardItem = styled.div<{ $rarity: CardRarity }>`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${p => RARITY_COLOR[p.$rarity]}44;
  background-color: #141414;
`;

const CardImageWrapper = styled.div`
  position: relative;
  cursor: pointer;
`;

const CardImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const CardImagePlaceholder = styled.div`
  aspect-ratio: 2/3;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255,255,255,0.04);
  color: rgba(255,255,255,0.2);
  font-size: 11px;
`;

const RarityBadge = styled.span<{ $rarity: CardRarity }>`
  position: absolute;
  top: 5px;
  left: 5px;
  font-size: 8px;
  font-weight: 700;
  padding: 2px 5px;
  border-radius: 3px;
  color: #111;
  background-color: ${p => RARITY_COLOR[p.$rarity]};
  text-transform: uppercase;
`;

const CardInfo = styled.div`
  padding: 8px 10px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const WrestlerName = styled.div`
  font-size: 11px;
  color: rgba(255,255,255,0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-top: auto;
`;

const PriceTag = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: gold;
`;

const BuyBtn = styled.button<{ $disabled?: boolean }>`
  padding: 5px 10px;
  border-radius: 5px;
  border: 1px solid ${p => p.$disabled ? 'rgba(255,255,255,0.1)' : 'rgba(255,215,0,0.6)'};
  background-color: ${p => p.$disabled ? 'transparent' : 'rgba(255,215,0,0.1)'};
  color: ${p => p.$disabled ? 'rgba(255,255,255,0.2)' : 'gold'};
  font-size: 11px;
  font-weight: 700;
  cursor: ${p => p.$disabled ? 'default' : 'pointer'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 0;
  color: rgba(255,255,255,0.3);
  font-size: 13px;
  font-style: italic;
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255,255,255,0.4);
  margin-top: 40px;
`;

// ─── Confirm modal ────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0,0,0,0.8);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 200;
`;

const Sheet = styled.div`
  background-color: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 16px 16px 0 0;
  width: 100%;
  max-width: 500px;
  padding: 24px 20px 40px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SheetTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #fff;
`;

const SheetRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: rgba(255,255,255,0.5);
  span { color: #fff; font-weight: 600; }
`;

const SheetPrice = styled.div`
  font-size: 24px;
  font-weight: 800;
  color: gold;
  text-align: center;
`;

const SheetBtns = styled.div`
  display: flex;
  gap: 10px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 12px 0;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.14);
  background: transparent;
  color: rgba(255,255,255,0.5);
  font-size: 14px;
  cursor: pointer;
`;

const ConfirmBuyBtn = styled.button<{ $loading: boolean }>`
  flex: 1;
  padding: 12px 0;
  border-radius: 8px;
  border: 1px solid ${p => p.$loading ? 'rgba(255,255,255,0.1)' : 'gold'};
  background-color: ${p => p.$loading ? 'transparent' : 'rgba(255,215,0,0.14)'};
  color: ${p => p.$loading ? 'rgba(255,255,255,0.3)' : 'gold'};
  font-size: 14px;
  font-weight: 700;
  cursor: ${p => p.$loading ? 'default' : 'pointer'};
`;

const FeedbackMsg = styled.div<{ $type: 'success' | 'error' }>`
  font-size: 13px;
  color: ${p => p.$type === 'success' ? '#4caf6f' : '#e05555'};
  text-align: center;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const CardShopMobile: React.FC = () => {
    const dispatch = useAppDispatch();
    const user = useAppSelector(s => s.auth.user ?? s.auth.userLoggedState);
    const isAuthenticated = !!user;
    const credits = user?.card_account?.credits ?? null;

    const [cards, setCards] = useState<Card[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterRarity, setFilterRarity] = useState<CardRarity | ''>('');
    const [filterExpansion, setFilterExpansion] = useState<number | ''>('');
    const [sortPrice, setSortPrice] = useState<'asc' | 'desc'>('asc');

    const [detailCard, setDetailCard] = useState<Card | null>(null);
    const [buyTarget, setBuyTarget] = useState<Card | null>(null);
    const [buying, setBuying] = useState(false);
    const [feedback, setFeedback] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

    useEffect(() => {
        let isMounted = true;
        apiGetShopCards()
            .then(res => { if (isMounted) setCards(res.data ?? []); })
            .catch(() => { if (isMounted) setCards([]); })
            .finally(() => { if (isMounted) setIsLoading(false); });
        return () => { isMounted = false; };
    }, []);

    const expansionOptions = useMemo(() => {
        const map = new Map<number, string>();
        cards.forEach(c => c.expansions.forEach(e => map.set(e.id, e.name)));
        return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
    }, [cards]);

    const filtered = useMemo(() => {
        return cards
            .filter(c => {
                if (filterRarity && c.rarity !== filterRarity) return false;
                if (filterExpansion && !c.expansions.some(e => e.id === filterExpansion)) return false;
                return true;
            })
            .sort((a, b) => sortPrice === 'asc' ? (a.price ?? 0) - (b.price ?? 0) : (b.price ?? 0) - (a.price ?? 0));
    }, [cards, filterRarity, filterExpansion, sortPrice]);

    const handleBuy = async () => {
        if (!buyTarget || buying) return;
        setBuying(true);
        setFeedback(null);
        try {
            const res = await apiBuyCard(buyTarget.id);
            setFeedback({ msg: res.message, type: 'success' });
            if (user?.card_account) {
                dispatch(updateCardAccount({ ...user.card_account, credits: res.credits_remaining }));
            }
        } catch (err: any) {
            setFeedback({ msg: err?.message ?? 'Acquisto non riuscito.', type: 'error' });
        } finally {
            setBuying(false);
        }
    };

    const imgUrl = (path: string | null) =>
        path ? (path.startsWith('http') ? path : process.env.REACT_APP_STORAGE_URL + path) : null;

    return (
        <Container>
            <PageTitle>Negozio Carte</PageTitle>
            <PageSub>Acquista singole carte con i tuoi crediti</PageSub>

            <FiltersColumn>
                <FilterSelect value={filterRarity} onChange={e => setFilterRarity(e.target.value as CardRarity | '')}>
                    <option value="">Tutte le rarità</option>
                    {RARITIES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                </FilterSelect>
                <FilterSelect value={filterExpansion} onChange={e => setFilterExpansion(e.target.value ? Number(e.target.value) : '')}>
                    <option value="">Tutte le espansioni</option>
                    {expansionOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </FilterSelect>
                <FilterSelect value={sortPrice} onChange={e => setSortPrice(e.target.value as 'asc' | 'desc')}>
                    <option value="asc">Prezzo: crescente</option>
                    <option value="desc">Prezzo: decrescente</option>
                </FilterSelect>
            </FiltersColumn>

            {!isLoading && <ResultCount>{filtered.length} carte disponibili</ResultCount>}

            {isLoading && <StatusText>Caricamento...</StatusText>}
            {!isLoading && filtered.length === 0 && <EmptyState>Nessuna carta disponibile nel negozio.</EmptyState>}

            {!isLoading && filtered.length > 0 && (
                <Grid>
                    {filtered.map(card => {
                        const src = imgUrl(card.fronte_path);
                        const canAfford = credits !== null && card.price !== null && credits >= card.price;
                        return (
                            <CardItem key={card.id} $rarity={card.rarity}>
                                <CardImageWrapper onClick={() => setDetailCard(card)}>
                                    {src
                                        ? <CardImage src={src} alt={`Carta ${card.id}`} />
                                        : <CardImagePlaceholder>No image</CardImagePlaceholder>
                                    }
                                    <RarityBadge $rarity={card.rarity}>{card.rarity}</RarityBadge>
                                </CardImageWrapper>
                                <CardInfo>
                                    <WrestlerName>
                                        {card.wrestlers.length > 0 ? card.wrestlers.map(w => w.name).join(', ') : '—'}
                                    </WrestlerName>
                                    <PriceRow>
                                        <PriceTag>{card.price} cr.</PriceTag>
                                        <BuyBtn
                                            $disabled={!isAuthenticated || !canAfford}
                                            disabled={!isAuthenticated || !canAfford}
                                            onClick={() => { setFeedback(null); setBuyTarget(card); }}
                                        >
                                            {!isAuthenticated ? 'Accedi' : !canAfford ? '—' : 'Acquista'}
                                        </BuyBtn>
                                    </PriceRow>
                                </CardInfo>
                            </CardItem>
                        );
                    })}
                </Grid>
            )}

            {buyTarget && (
                <Overlay onClick={e => { if (e.target === e.currentTarget && !buying) { setBuyTarget(null); setFeedback(null); } }}>
                    <Sheet>
                        <SheetTitle>Conferma acquisto</SheetTitle>
                        <SheetRow>
                            <span style={{ color: 'rgba(255,255,255,0.5)' }}>Carta</span>
                            <span>{buyTarget.wrestlers.length > 0 ? buyTarget.wrestlers.map(w => w.name).join(', ') : `#${buyTarget.id}`}</span>
                        </SheetRow>
                        <SheetRow>
                            <span style={{ color: 'rgba(255,255,255,0.5)' }}>I tuoi crediti</span>
                            <span>{credits?.toLocaleString() ?? '—'}</span>
                        </SheetRow>
                        <SheetPrice>- {buyTarget.price} crediti</SheetPrice>
                        {feedback && <FeedbackMsg $type={feedback.type}>{feedback.msg}</FeedbackMsg>}
                        <SheetBtns>
                            <CancelBtn onClick={() => { setBuyTarget(null); setFeedback(null); }} disabled={buying}>Annulla</CancelBtn>
                            <ConfirmBuyBtn $loading={buying} disabled={buying || !!feedback} onClick={handleBuy}>
                                {buying ? 'Acquisto...' : 'Conferma'}
                            </ConfirmBuyBtn>
                        </SheetBtns>
                    </Sheet>
                </Overlay>
            )}

            {detailCard && (
                <CardDetailModal
                    card={detailCard}
                    ownedQuantity={0}
                    isAuthenticated={isAuthenticated}
                    onClose={() => setDetailCard(null)}
                />
            )}
        </Container>
    );
};

export default CardShopMobile;
